// Image & Text Slideshow - Optimized
class ImageTextSlideshow extends HTMLElement {
  constructor() {
    super();
    this.swiperContainer = this.querySelector('.swiper') || this.querySelector('.slideshow__slider');
    this.swiperInstance = null;
    this.observer = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (!this.swiperContainer) {
      console.warn('⚠️ Swiper container not found');
      return;
    }

    // 使用 IntersectionObserver 延迟初始化
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.swiperInstance) {
            this.createSwiper();
            this.observer.disconnect();
          }
        });
      }, {
        rootMargin: '200px',
        threshold: 0.01,
      });
      this.observer.observe(this.swiperContainer);
    } else {
      // 降级：直接初始化
      this.createSwiper();
    }
  }

  createSwiper() {
    if (!this.swiperContainer || this.swiperInstance) return;

    // 检查 Swiper 是否已加载
    if (typeof Swiper === 'undefined') {
      const checkInterval = setInterval(() => {
        if (typeof Swiper !== 'undefined') {
          clearInterval(checkInterval);
          this._buildSwiper();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        if (typeof Swiper === 'undefined') {
          console.warn('⚠️ Swiper failed to load, using fallback');
          this.fallbackToNativeScroll();
        }
      }, 5000);
      return;
    }

    this._buildSwiper();
  }

  _buildSwiper() {
    const autoplay = this.dataset.autoplay === 'true';
    const autoplaySpeed = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;

    const prevEl = this.swiperContainer.querySelector('.swiper-button-prev');
    const nextEl = this.swiperContainer.querySelector('.swiper-button-next');
    const paginationEl = this.swiperContainer.querySelector('.swiper-pagination');

    const config = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 600,
      loop: false,
      grabCursor: true,
      touchRatio: 1.2,
      resistance: true,
      resistanceRatio: 0.85,
      touchStartPreventDefault: true,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      autoplay: autoplay ? {
        delay: autoplaySpeed,
        disableOnInteraction: true,
        pauseOnMouseEnter: true,
        waitForTransition: true,
      } : false,
      on: {
        slideChange: () => {
          this.dispatchEvent(new CustomEvent('slideChange', {
            detail: {
              activeIndex: this.swiperInstance?.activeIndex || 0,
              realIndex: this.swiperInstance?.realIndex || 0,
            },
            bubbles: true,
          }));
        },
      },
    };

    if (prevEl && nextEl) {
      config.navigation = {
        nextEl: nextEl,
        prevEl: prevEl,
        disabledClass: 'swiper-button-disabled',
      };
    }

    if (paginationEl) {
      config.pagination = {
        el: paginationEl,
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
      };
    }

    this.swiperInstance = new Swiper(this.swiperContainer, config);
    this.swiperContainer.swiper = this.swiperInstance;

    console.log('✅ ImageTextSlideshow initialized');
  }

  fallbackToNativeScroll() {
    const slider = this.swiperContainer;
    if (!slider) return;

    slider.style.overflowX = 'auto';
    slider.style.scrollSnapType = 'x mandatory';
    slider.style.display = 'flex';
    slider.style.scrollBehavior = 'smooth';
    slider.style.webkitOverflowScrolling = 'touch';

    const slides = slider.querySelectorAll('.swiper-slide');
    slides.forEach(slide => {
      slide.style.flex = '0 0 100%';
      slide.style.scrollSnapAlign = 'start';
    });

    // 隐藏导航
    const prev = slider.querySelector('.swiper-button-prev');
    const next = slider.querySelector('.swiper-button-next');
    if (prev) prev.style.display = 'none';
    if (next) next.style.display = 'none';
  }

  nextSlide() {
    if (this.swiperInstance) {
      this.swiperInstance.slideNext();
    } else {
      const slider = this.swiperContainer;
      if (slider) {
        slider.scrollBy({ left: slider.clientWidth, behavior: 'smooth' });
      }
    }
  }

  prevSlide() {
    if (this.swiperInstance) {
      this.swiperInstance.slidePrev();
    } else {
      const slider = this.swiperContainer;
      if (slider) {
        slider.scrollBy({ left: -slider.clientWidth, behavior: 'smooth' });
      }
    }
  }

  goToSlide(index) {
    if (this.swiperInstance) {
      this.swiperInstance.slideTo(index);
    } else {
      const slider = this.swiperContainer;
      if (slider) {
        slider.scrollTo({ left: index * slider.clientWidth, behavior: 'smooth' });
      }
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  disconnectedCallback() {
    this.destroy();
  }
}

customElements.define('image-text-slideshow', ImageTextSlideshow);